import { DataColumn, ExportFormat, ExportOptions, GenericWarning, KpiWithChangeColumn } from '../../types';
import { AvailableTemplate, ColumnType, DataCell, DataTable, ExcelExportDto } from '@ecosystem/export-api';
import { SegmentedChartData, checkChartDataHasChanges, labelToString } from './data';
import { TableColumn } from '@ecosystem/export-api/dist/dto/data-table';
import { getExportContext, mapValueToDataCell } from '../../export';
import { Formatting } from '../../data/formatting';
import { getGlobalWarningMessages } from '../utils';

export const chartDataToExportData = (
  exportFormat: ExportFormat,
  { fileHeaderSettings, fileName }: ExportOptions,
  chartData: SegmentedChartData,
  xColumn: DataColumn,
  yColumn: DataColumn,
  currency: string,
  globalWarnings: GenericWarning[] = []
): ExcelExportDto => {
  switch (exportFormat) {
    case ExportFormat.Xlsx:
      return {
        template: AvailableTemplate.Excel,
        name: fileName,
        worksheets: [
          {
            context: [...getExportContext(fileHeaderSettings), ...getGlobalWarningMessages(globalWarnings)],
            worksheetName: 'segmentation',
            data: createWorksheetData(chartData, xColumn, yColumn, currency),
          },
        ],
      };
    default:
      throw new Error("Export format isn't supported");
  }
};

const createSecondHeaderRow = (chartData: SegmentedChartData, xColumn: DataColumn, yColumn: DataColumn): DataCell[] => {
  const hasChanges = checkChartDataHasChanges(chartData);
  return [
    {
      type: ColumnType.Text,
      value: yColumn.label,
    },
    {
      type: ColumnType.Text,
      value: xColumn.label,
    },
    ...chartData.segmentDataSets.reduce(acc => {
      acc.push(
        ...chartData.dataLabels.map(label => ({
          type: ColumnType.Text,
          value: labelToString(label),
        }))
      );
      if (hasChanges) {
        acc.push(
          ...chartData.dataLabels
            .map((label, idx, all) => ({
              type: ColumnType.Text,
              value: idx > 0 ? `${yColumn.label} Change ${labelToString(all[idx - 1])} vs ${labelToString(label)}` : '',
            }))
            .filter(entry => entry.value !== '')
        );
      }
      return acc;
    }, []),
  ];
};

const createFirstHeaderRow = (chartData: SegmentedChartData, xColumn: DataColumn): TableColumn[] => {
  const periodCount = chartData.dataLabels.length;
  const hasChanges = checkChartDataHasChanges(chartData);
  const requiredColumns = hasChanges ? 2 * periodCount - 1 : periodCount;
  return [
    { type: ColumnType.Text, header: 'KPI' },
    { type: ColumnType.Text, header: xColumn.label },
    ...chartData.segmentDataSets.reduce((acc, current) => {
      for (let i = 0; i < requiredColumns; i++) {
        acc.push({ style: { width: 17 }, type: ColumnType.Text, header: current.label });
      }
      return acc;
    }, []),
  ];
};

const createTotalMarketRow = (chartData: SegmentedChartData, xColumn: DataColumn, yColumn: DataColumn) => {
  const changeFormatHint = (yColumn as KpiWithChangeColumn).changeFormatHint;
  const hasChanges = checkChartDataHasChanges(chartData);
  return [
    [
      {
        type: ColumnType.Text,
        value: yColumn.label,
      },
      {
        type: ColumnType.Text,
        value: 'Market',
      },
      ...chartData.segmentDataSets.reduce((acc, current) => {
        acc.push(
          ...chartData.dataLabels.map((_label, idx) => {
            const value = current.totals?.values.raw[idx];
            return mapValueToDataCell(value, yColumn.formatHint, yColumn.dataType);
          })
        );
        if (hasChanges) {
          acc.push(
            ...chartData.dataLabels.slice(1).map((_label, idx) => {
              const value = current.totals?.changes?.raw[idx + 1];
              return mapValueToDataCell(value, changeFormatHint, yColumn.dataType);
            })
          );
        }
        return acc;
      }, []),
    ],
  ];
};

const createDataRows = (chartData: SegmentedChartData, xColumn: DataColumn, yColumn: DataColumn) => {
  const allXColumnIds = chartData.segmentDataSets.reduce((acc, current) => {
    current.dataSets.forEach(dataSet => (acc[dataSet.uniqueId] = true));
    return acc;
  }, {});

  const changeFormatHint = (yColumn as KpiWithChangeColumn).changeFormatHint;
  const hasChanges = checkChartDataHasChanges(chartData);

  const final = [
    ...Object.keys(allXColumnIds).map(id => {
      return chartData.segmentDataSets.reduce((acc, current) => {
        const dataSet = current.dataSets.find(dataSet => dataSet.uniqueId === id);
        if (acc.length === 0) {
          acc.push({ type: ColumnType.Text, value: yColumn.label });
          acc.push({ type: ColumnType.Text, value: dataSet.label });
        }
        acc.push(
          ...chartData.dataLabels.map((_label, idx) => {
            const value = dataSet?.values.raw[idx];
            return mapValueToDataCell(value, yColumn.formatHint, yColumn.dataType);
          })
        );
        if (hasChanges) {
          acc.push(
            ...chartData.dataLabels.slice(1).map((_label, idx) => {
              const value = dataSet?.changes?.raw[idx + 1];
              return mapValueToDataCell(value, changeFormatHint, yColumn.dataType);
            })
          );
        }
        return acc;
      }, [] as Array<DataCell>);
    }),
  ];
  return final;
};
export const createWorksheetData = (
  chartData: SegmentedChartData,
  xColumn: DataColumn,
  yColumn: DataColumn,
  currency: string
): DataTable => {
  return {
    currencySymbol: ' ' + (Formatting.getCurrencySymbol(currency) || currency) + ' ',
    dataColumns: createFirstHeaderRow(chartData, xColumn),
    summaryRows: [
      createSecondHeaderRow(chartData, xColumn, yColumn),
      ...(chartData.segmentDataSets[0].totals ? createTotalMarketRow(chartData, xColumn, yColumn) : []),
    ],
    dataRows: createDataRows(chartData, xColumn, yColumn),
  };
};
